// AppointmentAutomations.jsx
// UI for managing appointment confirmation/reminder/no-show templates.
// Toggle on/off, edit template text, preview placeholders.

import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";

const PLACEHOLDERS = [
  { key: "{{first_name}}", desc: "Customer first name" },
  { key: "{{full_name}}", desc: "Customer full name" },
  { key: "{{service}}", desc: "Service name (e.g. 'Hydration Facial')" },
  { key: "{{date_pretty}}", desc: "Pretty date (e.g. 'Monday, May 13')" },
  { key: "{{time_pretty}}", desc: "Pretty time (e.g. '11:00 AM')" },
  { key: "{{date}}", desc: "Raw date (YYYY-MM-DD)" },
  { key: "{{time}}", desc: "Raw time (HH:MM)" },
];

export default function AppointmentAutomations() {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editSubject, setEditSubject] = useState("");
  const [saveStatus, setSaveStatus] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("appointment_automations")
      .select("*")
      .order("trigger_type")
      .order("hours_offset");
    setAutomations(data || []);
    setLoading(false);
  };

  const toggleEnabled = async (auto) => {
    const newVal = !auto.enabled;
    setAutomations(automations.map(a => a.id === auto.id ? { ...a, enabled: newVal } : a));
    const { error } = await supabase
      .from("appointment_automations")
      .update({ enabled: newVal, updated_at: new Date().toISOString() })
      .eq("id", auto.id);
    if (error) {
      alert("Failed: " + error.message);
      load();
    }
  };

  const startEdit = (auto) => {
    setEditingId(auto.id);
    setEditText(auto.template_text);
    setEditSubject(auto.subject || "");
  };

  const saveEdit = async (auto) => {
    setSaveStatus({ ...saveStatus, [auto.id]: "saving" });
    const update = { template_text: editText, updated_at: new Date().toISOString() };
    if (auto.channel === "email") update.subject = editSubject;
    const { error } = await supabase
      .from("appointment_automations")
      .update(update)
      .eq("id", auto.id);
    if (error) {
      setSaveStatus({ ...saveStatus, [auto.id]: "error" });
      alert("Failed: " + error.message);
    } else {
      setSaveStatus({ ...saveStatus, [auto.id]: "saved" });
      setEditingId(null);
      load();
      setTimeout(() => setSaveStatus(s => ({ ...s, [auto.id]: null })), 2000);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
    setEditSubject("");
  };

  const previewMessage = (template) => {
    return template
      .replace(/\{\{first_name\}\}/g, "Or")
      .replace(/\{\{full_name\}\}/g, "Or Atarzada")
      .replace(/\{\{service\}\}/g, "Hydration Facial")
      .replace(/\{\{date_pretty\}\}/g, "Monday, May 13")
      .replace(/\{\{time_pretty\}\}/g, "11:00 AM")
      .replace(/\{\{date\}\}/g, "2026-05-13")
      .replace(/\{\{time\}\}/g, "11:00");
  };

  if (loading) return <div style={{ padding: 20 }}>Loading automations…</div>;

  return (
    <div style={{ padding: 20, maxWidth: 900 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22 }}>📅 Appointment Automations</h2>
        <p style={{ color: "#666", marginTop: 4 }}>
          Edit templates for confirmation, reminders, and no-show recovery. Toggle to enable/disable.
        </p>
      </div>

      <div style={{ background: "#f4f8ff", padding: 12, borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
        <b>Available placeholders:</b>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {PLACEHOLDERS.map(p => (
            <code key={p.key} title={p.desc} style={{ background: "#e0ebff", padding: "2px 6px", borderRadius: 4, cursor: "help" }}>
              {p.key}
            </code>
          ))}
        </div>
      </div>

      {automations.map(auto => {
        const isEditing = editingId === auto.id;
        const channelIcon = auto.channel === "sms" ? "💬" : "📧";
        const triggerLabel = auto.trigger_type === "on_booking" ? "Immediate" :
                             auto.trigger_type === "before_appointment" ? `${Math.abs(auto.hours_offset)}h before` :
                             `${auto.hours_offset}h after`;
        return (
          <div key={auto.id} style={{
            border: "1px solid #ddd", borderRadius: 10, padding: 16, marginBottom: 14,
            background: auto.enabled ? "#fff" : "#f7f7f7", opacity: auto.enabled ? 1 : 0.7
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <h3 style={{ margin: 0, fontSize: 16 }}>{auto.display_name}</h3>
                  <span style={{ background: "#eee", padding: "2px 8px", borderRadius: 4, fontSize: 11 }}>
                    {channelIcon} {auto.channel.toUpperCase()}
                  </span>
                  <span style={{ background: "#fff3d6", padding: "2px 8px", borderRadius: 4, fontSize: 11 }}>
                    ⏱ {triggerLabel}
                  </span>
                  {auto.only_if_no_reply && (
                    <span style={{ background: "#ffe0e0", padding: "2px 8px", borderRadius: 4, fontSize: 11 }}>
                      ⚠ Only if no reply to earlier reminder
                    </span>
                  )}
                </div>
                {auto.description && (
                  <p style={{ margin: "4px 0", color: "#666", fontSize: 13 }}>{auto.description}</p>
                )}
              </div>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={auto.enabled}
                  onChange={() => toggleEnabled(auto)}
                  style={{ display: "none" }}
                />
                <div style={{
                  width: 50, height: 26, borderRadius: 13,
                  background: auto.enabled ? "#22c55e" : "#ccc",
                  position: "relative", transition: "all 0.2s"
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: 11, background: "#fff",
                    position: "absolute", top: 2, left: auto.enabled ? 26 : 2,
                    transition: "all 0.2s"
                  }} />
                </div>
              </label>
            </div>

            {/* Email subject */}
            {auto.channel === "email" && (
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>SUBJECT LINE</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    style={{ width: "100%", padding: 8, fontSize: 14, border: "1px solid #ccc", borderRadius: 6, marginTop: 4 }}
                  />
                ) : (
                  <div style={{ padding: 8, background: "#fafafa", borderRadius: 6, marginTop: 4, fontSize: 14 }}>
                    {auto.subject || "(no subject set)"}
                  </div>
                )}
              </div>
            )}

            {/* Template */}
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>MESSAGE TEMPLATE</label>
              {isEditing ? (
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={auto.channel === "email" ? 10 : 4}
                  style={{
                    width: "100%", padding: 10, fontSize: 14,
                    border: "1px solid #ccc", borderRadius: 6, marginTop: 4,
                    fontFamily: "inherit", resize: "vertical"
                  }}
                />
              ) : (
                <pre style={{
                  padding: 10, background: "#fafafa", borderRadius: 6, marginTop: 4,
                  fontSize: 13, whiteSpace: "pre-wrap", fontFamily: "inherit", margin: "4px 0 0 0"
                }}>{auto.template_text}</pre>
              )}
            </div>

            {/* Preview */}
            {!isEditing && (
              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, color: "#666" }}>👀 Preview how it looks</summary>
                <div style={{ marginTop: 8, padding: 12, background: "#e8f5e9", borderRadius: 8, fontSize: 13, whiteSpace: "pre-wrap" }}>
                  {previewMessage(auto.template_text)}
                </div>
              </details>
            )}

            {/* Actions */}
            <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
              {!isEditing ? (
                <button
                  onClick={() => startEdit(auto)}
                  style={{ padding: "6px 14px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}
                >✏️ Edit</button>
              ) : (
                <>
                  <button
                    onClick={() => saveEdit(auto)}
                    style={{ padding: "6px 14px", background: "#22c55e", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}
                  >💾 Save</button>
                  <button
                    onClick={cancelEdit}
                    style={{ padding: "6px 14px", background: "#eee", color: "#333", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}
                  >Cancel</button>
                </>
              )}
              {saveStatus[auto.id] === "saved" && <span style={{ color: "#22c55e", fontSize: 13 }}>✓ Saved</span>}
              {saveStatus[auto.id] === "saving" && <span style={{ color: "#999", fontSize: 13 }}>Saving…</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

